import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Download, 
  Users, 
  GraduationCap,
  Search,
  Plus,
  Edit,
  Eye,
  Save,
  X,
  Loader2,
  Upload,
  Trash2
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getBatches, 
  getBatchEnrollments,
  getIDCards,
  saveIDCard,
  deleteIDCard,
  updateIDCardStatus,
  searchUsers
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import R2Image from '../../components/R2Image';
import { refreshUrl } from '../../utils/urlRefreshHelper';

const IDCardManagement = () => {
  const { userProfile } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [idCards, setIdCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, student, teacher
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null); // Track which student is being generated
  const [selectedTemplate, setSelectedTemplate] = useState('template1');
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [studentsWithoutCards, setStudentsWithoutCards] = useState([]);
  const [showTeachersModal, setShowTeachersModal] = useState(false);
  const [teachersWithoutCards, setTeachersWithoutCards] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [studentForm, setStudentForm] = useState({
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    photo: '',
    photoFile: null,
    bloodGroup: '',
    emergencyContact: '',
    address: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    class: ''
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const cardRef = useRef(null);
  const cardFrontRef = useRef(null);
  const cardBackRef = useRef(null);

  // Template settings
  const [templateSettings, setTemplateSettings] = useState({
    instituteName: userProfile?.instituteName || userProfile?.coachingName || 'Institute Name',
    instituteLogo: userProfile?.instituteLogo || userProfile?.logo || '',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    showQRCode: true,
    showBarcode: true,
    showPhoto: true,
    validityPeriod: '1 year',
    includeAddress: true,
    includePhone: true,
    includeEmail: true,
    includeBloodGroup: false,
    includeEmergencyContact: true
  });

  const coachingId = userProfile?.coachingId;

  useEffect(() => {
    if (coachingId) {
      loadData();
    }
  }, [coachingId]);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchStudents(selectedBatch);
    } else {
      setStudents([]);
    }
  }, [selectedBatch]);

  useEffect(() => {
    // Find students without ID cards
    if (students.length > 0 && idCards.length > 0) {
      const studentsWithCards = new Set(
        idCards
          .filter(card => card.batchId === selectedBatch)
          .map(card => card.studentId)
      );
      
      const withoutCards = students.filter(
        student => !studentsWithCards.has(student.studentId)
      );
      
      setStudentsWithoutCards(withoutCards);
    } else if (students.length > 0) {
      setStudentsWithoutCards(students);
    } else {
      setStudentsWithoutCards([]);
    }
  }, [students, idCards, selectedBatch]);

  useEffect(() => {
    // Find teachers without ID cards
    if (teachers.length > 0 && idCards.length > 0) {
      const teachersWithCards = new Set(
        idCards
          .filter(card => card.type === 'teacher')
          .map(card => card.teacherId || card.studentId)
      );
      
      const withoutCards = teachers.filter(
        teacher => !teachersWithCards.has(teacher.id)
      );
      
      setTeachersWithoutCards(withoutCards);
    } else if (teachers.length > 0) {
      setTeachersWithoutCards(teachers);
    } else {
      setTeachersWithoutCards([]);
    }
  }, [teachers, idCards]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('Loading data for coachingId:', coachingId);
      
      // Load batches
      const batchesResult = await getBatches(coachingId);
      console.log('Batches result:', batchesResult);
      if (batchesResult.success) {
        setBatches(batchesResult.data || []);
      } else {
        console.error('Failed to load batches:', batchesResult.error);
      }

      // Load teachers for this coaching
      const teachersResult = await searchUsers('', { role: 'teacher' });
      console.log('Teachers result:', teachersResult);
      if (teachersResult.success) {
        // Filter teachers by coachingId
        const coachingTeachers = (teachersResult.data || []).filter(
          teacher => teacher.coachingId === coachingId
        );
        console.log('Coaching teachers:', coachingTeachers);
        setTeachers(coachingTeachers);
      } else {
        console.error('Failed to load teachers:', teachersResult.error);
      }

      // Load existing ID cards
      const cardsResult = await getIDCards(coachingId);
      console.log('ID Cards result:', cardsResult);
      if (cardsResult.success) {
        const cards = cardsResult.data || [];
        console.log('Loaded ID cards:', cards);
        setIdCards(cards);
        
        if (cards.length > 0) {
          toast.success(`Loaded ${cards.length} ID cards`);
        }
      } else {
        console.error('Failed to load ID cards:', cardsResult.error);
        toast.error('Failed to load ID cards: ' + cardsResult.error);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchStudents = async (batchId) => {
    try {
      console.log('Loading students for batch:', batchId);
      const result = await getBatchEnrollments(batchId);
      console.log('Enrollment result:', result);
      
      if (result.success) {
        // Map enrollment data to include studentId, studentName, studentEmail
        const mappedStudents = (result.data || []).map(enrollment => ({
          ...enrollment,
          studentId: enrollment.userId || enrollment.id,
          studentName: enrollment.userData?.fullName || enrollment.userData?.name || enrollment.email || 'Unknown',
          studentEmail: enrollment.userData?.email || enrollment.email || '',
          studentPhone: enrollment.userData?.phone || enrollment.phone || '',
          photo: enrollment.userData?.photoURL || enrollment.userData?.avatar || enrollment.photoURL || ''
        }));
        console.log('Mapped students:', mappedStudents);
        setStudents(mappedStudents);
        
        if (mappedStudents.length === 0) {
          toast.info('No students enrolled in this batch');
        } else {
          toast.success(`Loaded ${mappedStudents.length} students`);
        }
      } else {
        console.error('Failed to load students:', result.error);
        toast.error(result.error || 'Failed to load students');
      }
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students: ' + error.message);
    }
  };

  const generateQRCode = async (data) => {
    try {
      // Simple QR code generation using canvas
      const canvas = document.createElement('canvas');
      const size = 200;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      
      // Black border
      ctx.fillStyle = '#000000';
      ctx.fillRect(10, 10, size - 20, size - 20);
      
      // White inner area
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(15, 15, size - 30, size - 30);
      
      // Draw simple pattern (placeholder for actual QR)
      ctx.fillStyle = '#000000';
      const text = JSON.stringify(data);
      ctx.font = '10px monospace';
      ctx.fillText(text.substring(0, 20), 20, 100);
      
      return canvas.toDataURL();
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const generateBarcode = (text) => {
    // Simple barcode generation (Code 39 style)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 300;
    canvas.height = 80;
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = '#000000';
    ctx.font = '12px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(text, canvas.width / 2, canvas.height - 10);
    
    // Draw bars
    const barWidth = 3;
    let x = 20;
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i);
      const pattern = charCode % 2 === 0 ? [1, 0, 1, 0, 1] : [0, 1, 0, 1, 0];
      
      pattern.forEach(bar => {
        if (bar === 1) {
          ctx.fillRect(x, 10, barWidth, 50);
        }
        x += barWidth;
      });
      x += barWidth;
    }
    
    return canvas.toDataURL();
  };

  const generateIDCardNumber = (type, index) => {
    const year = new Date().getFullYear();
    const prefix = type === 'student' ? 'STU' : 'TCH';
    const number = String(index + 1).padStart(4, '0');
    return `${prefix}${year}${number}`;
  };

  const handleGenerateSingleCard = async (student) => {
    // Open edit modal first
    setEditingStudent(student);
    setEditingCard(null); // Not editing existing card
    setStudentForm({
      studentName: student.studentName || '',
      studentEmail: student.studentEmail || '',
      studentPhone: student.studentPhone || '',
      photo: student.photo || '',
      photoFile: null,
      bloodGroup: student.bloodGroup || '',
      emergencyContact: student.emergencyContact || '',
      address: student.address || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      dateOfBirth: student.dateOfBirth || '',
      class: batches.find(b => b.id === selectedBatch)?.name || ''
    });
    setShowEditModal(true);
  };

  const handleGenerateTeacherCard = async (teacher) => {
    // Open edit modal for teacher
    setEditingStudent({ studentId: teacher.id, isTeacher: true });
    setEditingCard(null);
    setStudentForm({
      studentName: teacher.fullName || teacher.displayName || teacher.email || '',
      studentEmail: teacher.email || '',
      studentPhone: teacher.phone || '',
      photo: teacher.photoURL || teacher.avatar || '',
      photoFile: null,
      bloodGroup: teacher.bloodGroup || '',
      emergencyContact: teacher.emergencyContact || '',
      address: teacher.address || '',
      fatherName: '',
      motherName: '',
      dateOfBirth: teacher.dateOfBirth || '',
      class: 'Teacher'
    });
    setShowEditModal(true);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setEditingStudent({ studentId: card.studentId });
    setStudentForm({
      studentName: card.studentName || '',
      studentEmail: card.studentEmail || '',
      studentPhone: card.studentPhone || '',
      photo: card.photo || '',
      photoFile: null,
      bloodGroup: card.bloodGroup || '',
      emergencyContact: card.emergencyContact || '',
      address: card.address || '',
      fatherName: card.fatherName || '',
      motherName: card.motherName || '',
      dateOfBirth: card.dateOfBirth || '',
      class: card.class || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteCard = (card) => {
    setCardToDelete(card);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!cardToDelete) return;

    try {
      setDeleting(true);
      const result = await deleteIDCard(cardToDelete.id);
      
      if (result.success) {
        toast.success(`ID card deleted for ${cardToDelete.studentName}`);
        await loadData(); // Reload data
        setShowDeleteConfirm(false);
        setCardToDelete(null);
      } else {
        toast.error('Failed to delete ID card: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting ID card:', error);
      toast.error('Failed to delete ID card: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      
      // Convert file to base64 for immediate local preview (avoids CORS issues)
      const reader = new FileReader();
      const localBase64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });

      // Upload to R2 Storage
      const path = `id-cards/${coachingId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, path);
      
      if (result.success) {
        // Use base64 for display, but store the R2 URL/fileKey for persistence
        setStudentForm({
          ...studentForm,
          photo: localBase64,  // Use base64 for immediate display (no CORS issues)
          photoFile: file,
          photoR2Url: result.url,  // Store R2 URL for saving to Firestore
          photoFileKey: result.fileKey || result.path  // Store fileKey for later retrieval
        });
        toast.success('Photo uploaded successfully!');
      } else {
        toast.error('Failed to upload photo: ' + result.error);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleConfirmGenerate = async () => {
    const isTeacher = editingStudent?.isTeacher;
    const entityName = isTeacher ? 'Teacher' : 'Student';
    
    // Validate required fields
    if (!studentForm.studentName || !studentForm.studentName.trim()) {
      toast.error(`${entityName} name is required`);
      return;
    }
    
    if (!studentForm.dateOfBirth) {
      toast.error('Date of birth is required');
      return;
    }
    
    if (!studentForm.class || !studentForm.class.trim()) {
      toast.error('Class/Batch is required');
      return;
    }
    
    if (!studentForm.photo) {
      toast.error(`${entityName} photo is required. Please upload a photo.`);
      return;
    }

    try {
      setGenerating(true);
      setGeneratingFor(editingStudent.studentId);
      
      console.log('User Profile:', userProfile);
      console.log('User Role:', userProfile?.role);
      console.log('Editing card:', editingCard);
      console.log('Coaching ID:', coachingId);
      console.log('Is Teacher:', isTeacher);
      
      if (!coachingId) {
        toast.error('Coaching ID not found. Please refresh and try again.');
        setGenerating(false);
        setGeneratingFor(null);
        return;
      }

      if (!userProfile?.role || (userProfile.role !== 'coaching_admin' && userProfile.role !== 'coaching_staff')) {
        toast.error('You must be a coaching admin or authorized staff to generate ID cards');
        setGenerating(false);
        setGeneratingFor(null);
        return;
      }

      // Check for specific section access if staff
      if (userProfile.role === 'coaching_staff' && !userProfile.accessibleSections?.includes('id-cards')) {
        toast.error('You do not have permission to generate ID cards');
        setGenerating(false);
        setGeneratingFor(null);
        return;
      }
      
      // Determine card type and number
      const cardType = isTeacher ? 'teacher' : 'student';
      const cardNumber = editingCard ? editingCard.cardNumber : generateIDCardNumber(cardType, idCards.length);
      
      const qrData = {
        id: cardNumber,
        name: studentForm.studentName,
        batch: editingCard ? editingCard.batchId : (isTeacher ? null : selectedBatch),
        type: cardType,
        institute: coachingId
      };
      
      const qrCode = await generateQRCode(qrData);
      const barcode = generateBarcode(cardNumber);
      
      // Refresh photo URL if it's expired
      // If photo was just uploaded, use the R2 URL for persistence; otherwise refresh existing URL
      let photoUrl = studentForm.photoR2Url || studentForm.photo;
      if (photoUrl && photoUrl.startsWith('http')) {
        const refreshedUrl = await refreshUrl(photoUrl);
        if (refreshedUrl) {
          photoUrl = refreshedUrl;
        }
      }
      
      // Refresh institute logo URL if it's expired
      let logoUrl = templateSettings.instituteLogo;
      if (logoUrl && logoUrl.startsWith('http')) {
        const refreshedLogo = await refreshUrl(logoUrl);
        if (refreshedLogo) {
          logoUrl = refreshedLogo;
        }
      }
      
      const cardData = {
        ...(editingCard && { id: editingCard.id }), // Include ID if editing
        cardNumber,
        type: cardType,
        ...(isTeacher ? { teacherId: editingStudent.studentId } : { studentId: editingStudent.studentId || editingStudent.userId }),
        studentName: studentForm.studentName.trim(),
        studentEmail: studentForm.studentEmail || '',
        studentPhone: studentForm.studentPhone || '',
        bloodGroup: studentForm.bloodGroup || '',
        emergencyContact: studentForm.emergencyContact || '',
        address: studentForm.address || '',
        fatherName: studentForm.fatherName || '',
        motherName: studentForm.motherName || '',
        dateOfBirth: studentForm.dateOfBirth,
        class: studentForm.class.trim(),
        ...(isTeacher ? {} : {
          batchId: editingCard ? editingCard.batchId : selectedBatch,
          batchName: editingCard ? editingCard.batchName : (batches.find(b => b.id === selectedBatch)?.name || '')
        }),
        photo: photoUrl,
        qrCode,
        barcode,
        validFrom: editingCard ? editingCard.validFrom : new Date().toISOString(),
        validUntil: editingCard ? editingCard.validUntil : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        coachingId: coachingId,
        template: selectedTemplate,
        templateSettings: {
          ...templateSettings,
          instituteLogo: logoUrl
        },
        status: editingCard ? editingCard.status : 'active'
      };
      
      console.log('Card data to save:', cardData);
      
      const result = await saveIDCard(cardData);
      console.log('Save result:', result);
      
      if (result.success) {
        toast.success(editingCard ? `ID card updated for ${studentForm.studentName}!` : `ID card generated for ${studentForm.studentName}!`);
        
        // Reload data to show the new/updated card
        console.log('Reloading data after card generation...');
        await loadData();
        
        // Close modals
        setShowEditModal(false);
        setShowStudentsModal(false);
        setShowTeachersModal(false);
        
        // Reset form
        setStudentForm({
          studentName: '',
          studentEmail: '',
          studentPhone: '',
          photo: '',
          photoFile: null,
          bloodGroup: '',
          emergencyContact: '',
          address: '',
          fatherName: '',
          motherName: '',
          dateOfBirth: '',
          class: ''
        });
        setEditingStudent(null);
        setEditingCard(null);
      } else {
        console.error('Failed to save card:', result);
        toast.error('Failed to ' + (editingCard ? 'update' : 'generate') + ' ID card: ' + (result.error || 'Unknown error'));
      }
      
    } catch (error) {
      console.error('Error generating ID card:', error);
      toast.error('Failed to ' + (editingCard ? 'update' : 'generate') + ' ID card: ' + error.message);
    } finally {
      setGenerating(false);
      setGeneratingFor(null);
    }
  };

  // Helper function to wait for all images to load
  const waitForImagesToLoad = (element) => {
    return new Promise((resolve) => {
      const images = Array.from(element.querySelectorAll('img'));
      
      if (images.length === 0) {
        resolve();
        return;
      }
      
      let loadedCount = 0;
      const totalImages = images.length;
      
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };
      
      images.forEach(img => {
        if (img.complete && img.naturalHeight !== 0) {
          checkComplete();
        } else {
          img.onload = checkComplete;
          img.onerror = checkComplete; // Continue even if image fails
        }
      });
    });
  };

  // Helper function to convert image to base64 with better error handling
  const imageToBase64 = async (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;

    try {
      // Direct fetch - CORS is now configured on the R2 bucket
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(blob);
        });
      }
    } catch (err) {
      console.warn('fetch imageToBase64 failed:', err.message);
    }

    // Fallback: canvas method for non-R2 URLs
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 200;
          canvas.height = img.naturalHeight || 200;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) { resolve(''); }
      };
      img.onerror = () => resolve('');
      img.src = url;
      setTimeout(() => resolve(''), 10000);
    });
  };

  const prepareCardImagesForDownload = async (cardContainer) => {
    if (!cardContainer) return 0;

    await waitForImagesToLoad(cardContainer);

    const images = Array.from(cardContainer.querySelectorAll('img'));
    let failedImages = 0;

    for (const img of images) {
      const originalSrc = img.src;
      if (!originalSrc) continue;

      console.log('Preparing image for download:', originalSrc.substring(0, 80));
      const base64 = await imageToBase64(originalSrc);

      if (base64) {
        img.src = base64;
        img.setAttribute('src', base64);
        img.style.display = '';
        console.log('✓ Image prepared as base64');
      } else {
        console.warn('Failed to prepare image, hiding it:', originalSrc.substring(0, 80));
        img.style.display = 'none';
        failedImages++;
      }
    }

    await new Promise(resolve => setTimeout(resolve, 300));
    return failedImages;
  };

  const downloadIDCard = async (card) => {
    try {
      setGenerating(true);
      
      // Refresh URLs in the card before displaying
      const refreshedCard = { ...card };
      if (refreshedCard.photo && refreshedCard.photo.startsWith('http')) {
        const freshPhotoUrl = await refreshUrl(refreshedCard.photo);
        if (freshPhotoUrl) {
          refreshedCard.photo = freshPhotoUrl;
        }
      }
      if (refreshedCard.templateSettings?.instituteLogo && refreshedCard.templateSettings.instituteLogo.startsWith('http')) {
        const freshLogoUrl = await refreshUrl(refreshedCard.templateSettings.instituteLogo);
        if (freshLogoUrl) {
          refreshedCard.templateSettings = {
            ...refreshedCard.templateSettings,
            instituteLogo: freshLogoUrl
          };
        }
      }

      // Pre-convert photo and logo to base64 to avoid CORS issues during html-to-image capture
      if (refreshedCard.photo && refreshedCard.photo.startsWith('http')) {
        const photoBase64 = await imageToBase64(refreshedCard.photo);
        if (photoBase64) {
          refreshedCard.photo = photoBase64;
          console.log('✓ Pre-converted photo to base64');
        } else {
          console.warn('⚠️ Failed to pre-convert photo to base64');
        }
      }
      if (refreshedCard.templateSettings?.instituteLogo && refreshedCard.templateSettings.instituteLogo.startsWith('http')) {
        const logoBase64 = await imageToBase64(refreshedCard.templateSettings.instituteLogo);
        if (logoBase64) {
          refreshedCard.templateSettings = {
            ...refreshedCard.templateSettings,
            instituteLogo: logoBase64
          };
          console.log('✓ Pre-converted logo to base64');
        }
      }

      // Ensure preview modal is open with the selected card
      if (!showPreviewModal || selectedCard?.id !== card.id) {
        setSelectedCard(refreshedCard);
        setShowPreviewModal(true);
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else {
        setSelectedCard(refreshedCard);
        await new Promise(resolve => setTimeout(resolve, 800));
      }
      
      // Check if refs are available
      if (!cardFrontRef.current || !cardBackRef.current) {
        console.error('Refs not available');
        toast.error('Failed to capture ID card. Please try again.');
        return;
      }

      console.log('=== Starting ID Card Download ===');
      
      // Wait for all images to load first
      console.log('Waiting for images to load...');
      await Promise.all([
        waitForImagesToLoad(cardFrontRef.current),
        waitForImagesToLoad(cardBackRef.current)
      ]);
      
      console.log('Preparing card images for download...');
      let failedImages = 0;
      failedImages += await prepareCardImagesForDownload(cardFrontRef.current);
      failedImages += await prepareCardImagesForDownload(cardBackRef.current);

      if (failedImages > 0) {
        console.warn(`⚠️ ${failedImages} image(s) failed to load and will be blank in the PDF`);
        toast('Some images could not be loaded. ID card will be generated without them.', { 
          icon: '⚠️',
          duration: 4000 
        });
      }

      console.log('Waiting for DOM to update with base64 images...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      console.log('Capturing front side...');
      const frontDataUrl = await toPng(cardFrontRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: false, // Don't add cache busting params
        skipFonts: true, // Skip font loading to avoid CORS
        filter: (node) => {
          // Filter out problematic nodes
          const tagName = node.tagName;
          if (tagName === 'LINK' || tagName === 'STYLE') {
            return false;
          }
          // Only include nodes that are part of the card
          return true;
        }
      });
      
      console.log('Capturing back side...');
      const backDataUrl = await toPng(cardBackRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: false, // Don't add cache busting params
        skipFonts: true, // Skip font loading to avoid CORS
        filter: (node) => {
          // Filter out problematic nodes
          const tagName = node.tagName;
          if (tagName === 'LINK' || tagName === 'STYLE') {
            return false;
          }
          // Only include nodes that are part of the card
          return true;
        }
      });
      
      console.log('Creating PDF...');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [85.6, 53.98]
      });
      
      pdf.addImage(frontDataUrl, 'PNG', 0, 0, 85.6, 53.98, undefined, 'FAST');
      pdf.addPage();
      pdf.addImage(backDataUrl, 'PNG', 0, 0, 85.6, 53.98, undefined, 'FAST');
      
      const fileName = `ID-Card-${card.cardNumber}-${card.studentName.replace(/\s+/g, '-')}.pdf`;
      pdf.save(fileName);
      
      console.log('=== Download Complete ===');
      toast.success('ID card downloaded successfully!');
      
    } catch (error) {
      console.error('Download error:', error);
      let errorMessage = 'Failed to download ID card';
      
      if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const downloadBulkIDCards = async () => {
    try {
      setGenerating(true);
      
      const filteredCards = idCards.filter(card => {
        if (filterType === 'all') return true;
        return card.type === filterType;
      });
      
      if (filteredCards.length === 0) {
        toast.error('No ID cards to download');
        return;
      }
      
      // Create PDF with A4 size for multiple cards
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      let isFirstPage = true;
      
      for (let i = 0; i < filteredCards.length; i++) {
        const card = filteredCards[i];
        
        if (!isFirstPage) pdf.addPage();
        isFirstPage = false;
        
        // Set card for preview
        const refreshedCard = { ...card };
        if (refreshedCard.photo && refreshedCard.photo.startsWith('http')) {
          const freshPhotoUrl = await refreshUrl(refreshedCard.photo);
          if (freshPhotoUrl) refreshedCard.photo = freshPhotoUrl;
        }
        if (refreshedCard.templateSettings?.instituteLogo && refreshedCard.templateSettings.instituteLogo.startsWith('http')) {
          const freshLogoUrl = await refreshUrl(refreshedCard.templateSettings.instituteLogo);
          if (freshLogoUrl) {
            refreshedCard.templateSettings = {
              ...refreshedCard.templateSettings,
              instituteLogo: freshLogoUrl
            };
          }
        }

        // Pre-convert photo and logo to base64 to avoid CORS issues
        if (refreshedCard.photo && refreshedCard.photo.startsWith('http')) {
          const photoBase64 = await imageToBase64(refreshedCard.photo);
          if (photoBase64) refreshedCard.photo = photoBase64;
        }
        if (refreshedCard.templateSettings?.instituteLogo && refreshedCard.templateSettings.instituteLogo.startsWith('http')) {
          const logoBase64 = await imageToBase64(refreshedCard.templateSettings.instituteLogo);
          if (logoBase64) {
            refreshedCard.templateSettings = {
              ...refreshedCard.templateSettings,
              instituteLogo: logoBase64
            };
          }
        }

        setSelectedCard(refreshedCard);
        setShowPreviewModal(true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (cardFrontRef.current && cardBackRef.current) {
          await prepareCardImagesForDownload(cardFrontRef.current);
          await prepareCardImagesForDownload(cardBackRef.current);

          const frontDataUrl = await toPng(cardFrontRef.current, {
            quality: 1.0,
            pixelRatio: 3,
            backgroundColor: '#ffffff',
            cacheBust: true,
            width: cardFrontRef.current.offsetWidth,
            height: cardFrontRef.current.offsetHeight
          });
          
          const backDataUrl = await toPng(cardBackRef.current, {
            quality: 1.0,
            pixelRatio: 3,
            backgroundColor: '#ffffff',
            cacheBust: true,
            width: cardBackRef.current.offsetWidth,
            height: cardBackRef.current.offsetHeight
          });
          
          // Add front and back side on same page with proper spacing
          // Front side at top
          pdf.addImage(frontDataUrl, 'PNG', 10, 10, 85.6, 53.98, undefined, 'FAST');
          // Back side below with some spacing
          pdf.addImage(backDataUrl, 'PNG', 10, 75, 85.6, 53.98, undefined, 'FAST');
        }
      }
      
      pdf.save(`ID-Cards-Bulk-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Downloaded ${filteredCards.length} ID cards!`);
      
    } catch (error) {
      console.error('Error downloading bulk cards:', error);
      toast.error('Failed to download bulk cards: ' + error.message);
    } finally {
      setGenerating(false);
      setShowPreviewModal(false);
    }
  };

  const IDCardTemplate1 = ({ card, showBack = false, customRef = null }) => (
    <div 
      ref={customRef || cardRef}
      className="w-[85.6mm] h-[53.98mm] bg-white rounded-lg shadow-lg overflow-hidden relative"
      style={{ 
        borderLeft: `4px solid ${templateSettings.primaryColor}`,
        fontFamily: 'Arial, sans-serif'
      }}
    >
      {!showBack ? (
        /* Front Side */
        <div className="h-full p-3 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            {templateSettings.instituteLogo && (
              <img 
                src={templateSettings.instituteLogo} 
                alt="Logo" 
                className="h-8 w-8 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div className="flex-1 text-center">
              <h3 className="text-xs font-bold" style={{ color: templateSettings.primaryColor }}>
                {templateSettings.instituteName}
              </h3>
              <p className="text-[10px] text-gray-600">
                {card.type === 'teacher' ? 'Teacher ID Card' : 'Student ID Card'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              {/* QR code removed for cleaner look */}
            </div>
          </div>

          {/* Content */}
          <div className="flex gap-2 flex-1">
            {/* Photo */}
            {templateSettings.showPhoto && card.photo && (
              <div className="w-20 h-24 bg-gray-200 rounded overflow-hidden flex-shrink-0 border-2 border-gray-300">
                <img 
                  src={card.photo} 
                  alt={card.studentName}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
              </div>
            )}

            {/* Details */}
            <div className="flex-1 text-[10px] space-y-1">
              <div>
                <p className="text-gray-600 text-[9px]">
                  {card.type === 'teacher' ? 'Teacher ID' : 'Student ID'}
                </p>
                <p className="font-mono font-bold text-[10px] text-blue-600 leading-tight">{card.cardNumber}</p>
              </div>
              <div>
                <p className="text-gray-600 text-[9px]">Name</p>
                <p className="font-bold text-xs leading-tight">{card.studentName}</p>
              </div>
              {card.fatherName && (
                <div>
                  <p className="text-gray-600 text-[9px]">Father's Name</p>
                  <p className="font-semibold text-[10px] leading-tight">{card.fatherName}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-1 pt-1">
                {card.dateOfBirth && (
                  <div>
                    <p className="text-gray-600 text-[9px]">DOB</p>
                    <p className="font-semibold text-[10px]">{new Date(card.dateOfBirth).toLocaleDateString('en-GB')}</p>
                  </div>
                )}
                {card.class && (
                  <div>
                    <p className="text-gray-600 text-[9px]">
                      {card.type === 'teacher' ? 'Designation' : 'Class'}
                    </p>
                    <p className="font-semibold text-[10px]">{card.class}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-1.5 border-t border-gray-200">
            <div className="flex justify-between items-center text-[9px]">
              <span className="text-gray-600">
                Valid Until: {new Date(card.validUntil).toLocaleDateString('en-GB')}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* Back Side */
        <div className="h-full p-3 flex flex-col bg-gradient-to-br from-gray-50 to-white">
          {/* Contact Details */}
          <div className="flex-1 text-[10px] space-y-2">
            {card.studentEmail && (
              <div>
                <p className="text-gray-600 text-[9px] font-semibold">Email</p>
                <p className="text-gray-800 break-all leading-tight">{card.studentEmail}</p>
              </div>
            )}
            {card.studentPhone && (
              <div>
                <p className="text-gray-600 text-[9px] font-semibold">Phone</p>
                <p className="text-gray-800 leading-tight">{card.studentPhone}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {card.bloodGroup && (
                <div>
                  <p className="text-gray-600 text-[9px] font-semibold">Blood Group</p>
                  <p className="text-gray-800 font-bold text-red-600 text-[11px]">{card.bloodGroup}</p>
                </div>
              )}
              {card.emergencyContact && (
                <div>
                  <p className="text-gray-600 text-[9px] font-semibold">Emergency</p>
                  <p className="text-gray-800 leading-tight">{card.emergencyContact}</p>
                </div>
              )}
            </div>
            {card.motherName && (
              <div>
                <p className="text-gray-600 text-[9px] font-semibold">Mother's Name</p>
                <p className="text-gray-800 font-semibold leading-tight">{card.motherName}</p>
              </div>
            )}
            {card.address && (
              <div className="flex-1">
                <p className="text-gray-600 text-[9px] font-semibold mb-1">Address</p>
                <p className="text-gray-800 leading-[1.4] break-words">{card.address}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const filteredCards = idCards.filter(card => {
    const matchesSearch = card.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         card.cardNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || card.type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="w-8 h-8 text-blue-600" />
          ID Card Management
        </h1>
        <p className="text-gray-600 mt-2">Generate and manage student & teacher ID cards</p>
        {idCards.length > 0 && (
          <p className="text-sm text-green-600 mt-1">
            Total ID Cards: {idCards.length}
          </p>
        )}
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-3">
            <select
              value={selectedBatch || ''}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Batch</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>

            {selectedBatch && (
              <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">{students.length} Students</span>
              </div>
            )}

            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="template1">Template 1 - Modern</option>
              <option value="template2">Template 2 - Classic</option>
              <option value="template3">Template 3 - Minimal</option>
            </select>
          </div>

          <div className="flex gap-3">
            {selectedBatch && studentsWithoutCards.length > 0 && (
              <button
                onClick={() => setShowStudentsModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Generate Student ID Cards ({studentsWithoutCards.length})
              </button>
            )}

            {teachersWithoutCards.length > 0 && (
              <button
                onClick={() => setShowTeachersModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Generate Teacher ID Cards ({teachersWithoutCards.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or ID number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({idCards.length})
            </button>
            <button
              onClick={() => setFilterType('student')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                filterType === 'student' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4" />
              Students ({idCards.filter(c => c.type === 'student').length})
            </button>
            <button
              onClick={() => setFilterType('teacher')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                filterType === 'teacher' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <GraduationCap className="w-4 h-4" />
              Teachers ({idCards.filter(c => c.type === 'teacher').length})
            </button>
          </div>
        </div>
      </div>

      {/* ID Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCards.map(card => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{card.studentName}</h3>
                <p className="text-sm text-gray-600 font-mono">{card.cardNumber}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                card.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {card.status}
              </span>
            </div>

            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <p><span className="font-medium">Batch:</span> {card.batchName}</p>
              <p><span className="font-medium">Valid Until:</span> {new Date(card.validUntil).toLocaleDateString()}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={async () => {
                  // Refresh URLs before preview
                  const refreshedCard = { ...card };
                  if (refreshedCard.photo && refreshedCard.photo.startsWith('http')) {
                    const freshPhotoUrl = await refreshUrl(refreshedCard.photo);
                    if (freshPhotoUrl) {
                      refreshedCard.photo = freshPhotoUrl;
                    }
                  }
                  if (refreshedCard.templateSettings?.instituteLogo && refreshedCard.templateSettings.instituteLogo.startsWith('http')) {
                    const freshLogoUrl = await refreshUrl(refreshedCard.templateSettings.instituteLogo);
                    if (freshLogoUrl) {
                      refreshedCard.templateSettings = {
                        ...refreshedCard.templateSettings,
                        instituteLogo: freshLogoUrl
                      };
                    }
                  }
                  setSelectedCard(refreshedCard);
                  setShowPreviewModal(true);
                }}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={() => handleEditCard(card)}
                className="flex-1 px-3 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 flex items-center justify-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => downloadIDCard(card)}
                className="flex-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => handleDeleteCard(card)}
                className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredCards.length === 0 && (
        <div className="text-center py-12">
          <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No ID Cards Found</h3>
          <p className="text-gray-600">Generate ID cards for your students and teachers</p>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">ID Card Preview</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2 text-center">Front Side</p>
                <div className="flex justify-center">
                  <IDCardTemplate1 card={selectedCard} showBack={false} customRef={cardFrontRef} />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2 text-center">Back Side</p>
                <div className="flex justify-center">
                  <IDCardTemplate1 card={selectedCard} showBack={true} customRef={cardBackRef} />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={() => downloadIDCard(selectedCard)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Both Sides
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Details Modal */}
      {showEditModal && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">
                {editingCard 
                  ? 'Edit ID Card Details' 
                  : editingStudent?.isTeacher 
                    ? 'Review & Edit Teacher Details' 
                    : 'Review & Edit Student Details'}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingStudent?.isTeacher ? 'Teacher Name *' : 'Student Name *'}
                </label>
                <input
                  type="text"
                  value={studentForm.studentName}
                  onChange={(e) => setStudentForm({...studentForm, studentName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={editingStudent?.isTeacher ? 'Enter teacher name' : 'Enter student name'}
                />
              </div>

              {!editingStudent?.isTeacher && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Name
                    </label>
                    <input
                      type="text"
                      value={studentForm.fatherName}
                      onChange={(e) => setStudentForm({...studentForm, fatherName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter father's name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Name
                    </label>
                    <input
                      type="text"
                      value={studentForm.motherName}
                      onChange={(e) => setStudentForm({...studentForm, motherName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter mother's name"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={studentForm.dateOfBirth}
                    onChange={(e) => setStudentForm({...studentForm, dateOfBirth: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingStudent?.isTeacher ? 'Designation *' : 'Class/Batch *'}
                  </label>
                  <input
                    type="text"
                    value={studentForm.class}
                    onChange={(e) => setStudentForm({...studentForm, class: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={editingStudent?.isTeacher ? 'e.g., Mathematics Teacher' : 'e.g., Class 10'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={studentForm.studentEmail}
                    onChange={(e) => setStudentForm({...studentForm, studentEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="student@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={studentForm.studentPhone}
                    onChange={(e) => setStudentForm({...studentForm, studentPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Group
                  </label>
                  <select
                    value={studentForm.bloodGroup}
                    onChange={(e) => setStudentForm({...studentForm, bloodGroup: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emergency Contact
                  </label>
                  <input
                    type="tel"
                    value={studentForm.emergencyContact}
                    onChange={(e) => setStudentForm({...studentForm, emergencyContact: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  value={studentForm.address}
                  onChange={(e) => setStudentForm({...studentForm, address: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingStudent?.isTeacher ? 'Teacher Photo *' : 'Student Photo *'}
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors">
                      <div className="flex flex-col items-center">
                        {uploadingPhoto ? (
                          <>
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-2" />
                            <p className="text-sm text-gray-600">Uploading...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">Click to upload photo</p>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                          </>
                        )}
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                  
                  {studentForm.photo && (
                    <div className="flex-shrink-0">
                      <img 
                        src={studentForm.photo} 
                        alt="Preview" 
                        className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          toast.error('Failed to load image');
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGenerate}
                disabled={generating || !studentForm.studentName || !studentForm.dateOfBirth || !studentForm.class || !studentForm.photo}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {editingCard ? 'Updating...' : 'Generating...'}
                  </>
                ) : (
                  <>
                    {editingCard ? <Save className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                    {editingCard ? 'Update ID Card' : 'Generate ID Card'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students Without Cards Modal */}
      {showStudentsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Students Without ID Cards</h3>
              <button
                onClick={() => setShowStudentsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {studentsWithoutCards.map((student, index) => (
                <div
                  key={student.studentId || index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    {student.photo ? (
                      <img
                        src={student.photo}
                        alt={student.studentName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900">{student.studentName}</h4>
                      <p className="text-sm text-gray-600">{student.studentEmail}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateSingleCard(student);
                    }}
                    disabled={generating && generatingFor === student.studentId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {generating && generatingFor === student.studentId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Generate
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {studentsWithoutCards.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">All students have ID cards!</p>
              </div>
            )}

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowStudentsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teachers Without Cards Modal */}
      {showTeachersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Teachers Without ID Cards</h3>
              <button
                onClick={() => setShowTeachersModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {teachersWithoutCards.map((teacher, index) => (
                <div
                  key={teacher.id || index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    {teacher.photoURL || teacher.avatar ? (
                      <R2Image
                        fileKey={teacher.photoURL || teacher.avatar}
                        alt={teacher.fullName || teacher.displayName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-purple-600" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900">{teacher.fullName || teacher.displayName || teacher.email}</h4>
                      <p className="text-sm text-gray-600">{teacher.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateTeacherCard(teacher);
                    }}
                    disabled={generating && generatingFor === teacher.id}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {generating && generatingFor === teacher.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Generate
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {teachersWithoutCards.length === 0 && (
              <div className="text-center py-8">
                <GraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">All teachers have ID cards!</p>
              </div>
            )}

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowTeachersModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Settings Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Customize Template</h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute Name
                </label>
                <input
                  type="text"
                  value={templateSettings.instituteName}
                  onChange={(e) => setTemplateSettings({...templateSettings, instituteName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <input
                  type="color"
                  value={templateSettings.primaryColor}
                  onChange={(e) => setTemplateSettings({...templateSettings, primaryColor: e.target.value})}
                  className="w-full h-10 rounded-lg cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={templateSettings.showQRCode}
                    onChange={(e) => setTemplateSettings({...templateSettings, showQRCode: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Show QR Code</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={templateSettings.showBarcode}
                    onChange={(e) => setTemplateSettings({...templateSettings, showBarcode: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Show Barcode</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={templateSettings.showPhoto}
                    onChange={(e) => setTemplateSettings({...templateSettings, showPhoto: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Show Photo</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={templateSettings.includePhone}
                    onChange={(e) => setTemplateSettings({...templateSettings, includePhone: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Include Phone Number</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  toast.success('Template settings saved!');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && cardToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-600">Delete ID Card</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                disabled={deleting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the ID card for:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">{cardToDelete.studentName}</p>
                <p className="text-sm text-gray-600 font-mono">{cardToDelete.cardNumber}</p>
                <p className="text-sm text-gray-600">{cardToDelete.batchName}</p>
              </div>
              <p className="text-red-600 text-sm mt-4">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete ID Card
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDCardManagement;
